import { useQuery } from '@apollo/client';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { GET_ORDAT_INTEGRATION_PARAMETER_DEFINITIONS } from './queries.ts';
import {
    ORDER_PUSH_MODE_PREORDER,
    ORDER_PUSH_MODE_WEBSHOP,
    type OrdatFormData,
    type OrdatIntegration,
    type OrdatParameterDefinition,
} from './types.ts';
import BooleanField from '../../../core/components/Fields/BooleanField.tsx';
import DecimalField from '../../../core/components/Fields/DecimalField.tsx';
import IntegerField from '../../../core/components/Fields/IntegerField.tsx';
import JsonField from '../../../core/components/Fields/JsonField.tsx';
import SelectField from '../../../core/components/Fields/SelectField.tsx';
import StringField from '../../../core/components/Fields/StringField.tsx';
import TextField from '../../../core/components/Fields/TextField.tsx';
import FormField from '../../../core/components/FormField.tsx';

export interface OrdatAdvancedFieldsProps {
    form: UseFormReturn<OrdatFormData>;
    integration?: OrdatIntegration;
}

/**
 * The rarely touched half of the ORDAT connector configuration. Collapsed by default so the
 * default form is just credentials, Mandant, GB/UB and the channels.
 */
const OrdatAdvancedFields = ({ form, integration }: OrdatAdvancedFieldsProps) => {
    const { t } = useTranslation('integrations');
    const formErrors = form.formState.errors;

    const { data: parameterDefinitionsData } = useQuery(GET_ORDAT_INTEGRATION_PARAMETER_DEFINITIONS);
    const parameterDefinitions: OrdatParameterDefinition[] =
        parameterDefinitionsData?.ordatIntegrationParameterDefinitions || [];

    const caBundleHelpText = integration?.hasCaBundle
        ? t('ordat.form.caBundleStored')
        : t('ordat.form.caBundleHelpText');

    return (
        <details className="border border-solid border-line-weak rounded-lg">
            <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-content-strong select-none">
                {t('ordat.form.advancedSection')}
            </summary>

            <div className="flex flex-col gap-6 border-t border-solid border-line-weak p-4">
                <Controller
                    name="proxy"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('ordat.form.proxy')}
                            error={formErrors.proxy}
                            helpText={t('ordat.form.proxyHelpText')}
                        >
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="caBundle"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('ordat.form.caBundle')}
                            error={formErrors.caBundle}
                            helpText={caBundleHelpText}
                        >
                            <TextField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="cultureName"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('ordat.form.cultureName')}
                            error={formErrors.cultureName}
                            helpText={t('ordat.form.cultureNameHelpText')}
                        >
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="orderPushMode"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('ordat.form.orderPushMode')}
                            error={formErrors.orderPushMode}
                            helpText={t('ordat.form.orderPushModeHelpText')}
                        >
                            <SelectField
                                {...field}
                                options={[
                                    {
                                        value: ORDER_PUSH_MODE_WEBSHOP,
                                        label: t('ordat.form.orderPushModeOptions.webshop'),
                                        description: t('ordat.form.orderPushModeOptions.webshopDescription'),
                                    },
                                    {
                                        value: ORDER_PUSH_MODE_PREORDER,
                                        label: t('ordat.form.orderPushModeOptions.preorder'),
                                        description: t('ordat.form.orderPushModeOptions.preorderDescription'),
                                    },
                                ]}
                                placeholder={t('ordat.form.orderPushModePlaceholder')}
                            />
                        </FormField>
                    )}
                />

                <Controller
                    name="defaultShipmentType"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('ordat.form.defaultShipmentType')}
                            error={formErrors.defaultShipmentType}
                            helpText={t('ordat.form.defaultShipmentTypeHelpText')}
                        >
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="headerTextEnabled"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('ordat.form.headerTextEnabled')}
                            error={formErrors.headerTextEnabled}
                            helpText={t('ordat.form.headerTextEnabledHelpText')}
                        >
                            <BooleanField {...field} value={field.value ? 'true' : ''} />
                        </FormField>
                    )}
                />

                <Controller
                    name="headerTextContextId"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('ordat.form.headerTextContextId')}
                            error={formErrors.headerTextContextId}
                            helpText={t('ordat.form.textContextIdHelpText')}
                        >
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="itemTextEnabled"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('ordat.form.itemTextEnabled')}
                            error={formErrors.itemTextEnabled}
                            helpText={t('ordat.form.itemTextEnabledHelpText')}
                        >
                            <BooleanField {...field} value={field.value ? 'true' : ''} />
                        </FormField>
                    )}
                />

                <Controller
                    name="itemTextContextId"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('ordat.form.itemTextContextId')}
                            error={formErrors.itemTextContextId}
                            helpText={t('ordat.form.textContextIdHelpText')}
                        >
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="itemAdditionalTextContextId"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('ordat.form.itemAdditionalTextContextId')}
                            error={formErrors.itemAdditionalTextContextId}
                            helpText={t('ordat.form.itemAdditionalTextContextIdHelpText')}
                        >
                            <IntegerField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="fallbackPartNumber"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('ordat.form.fallbackPartNumber')}
                            error={formErrors.fallbackPartNumber}
                            helpText={t('ordat.form.fallbackPartNumberHelpText')}
                        >
                            <StringField {...field} maxLength={12} />
                        </FormField>
                    )}
                />

                <Controller
                    name="sellablePartsOnly"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('ordat.form.sellablePartsOnly')}
                            error={formErrors.sellablePartsOnly}
                            helpText={t('ordat.form.sellablePartsOnlyHelpText')}
                        >
                            <BooleanField {...field} value={field.value ? 'true' : ''} />
                        </FormField>
                    )}
                />

                <Controller
                    name="attachSourceDocuments"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('ordat.form.attachSourceDocuments')}
                            error={formErrors.attachSourceDocuments}
                            helpText={t('ordat.form.attachSourceDocumentsHelpText')}
                        >
                            <BooleanField {...field} value={field.value ? 'true' : ''} />
                        </FormField>
                    )}
                />

                <Controller
                    name="priceCheckLowerThresholdPercentage"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('ordat.form.priceCheckLowerThresholdPercentage')}
                            error={formErrors.priceCheckLowerThresholdPercentage}
                            helpText={t('ordat.form.priceCheckLowerThresholdPercentageHelpText')}
                        >
                            <DecimalField {...field} value={(field.value ?? '').toString()} decimalSeparator="," />
                        </FormField>
                    )}
                />

                <Controller
                    name="priceCheckUpperThresholdPercentage"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('ordat.form.priceCheckUpperThresholdPercentage')}
                            error={formErrors.priceCheckUpperThresholdPercentage}
                            helpText={t('ordat.form.priceCheckUpperThresholdPercentageHelpText')}
                        >
                            <DecimalField {...field} value={(field.value ?? '').toString()} decimalSeparator="," />
                        </FormField>
                    )}
                />

                <Controller
                    name="useOutputMapping"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('ordat.form.useOutputMapping')}
                            error={formErrors.useOutputMapping}
                            helpText={t('ordat.form.useOutputMappingHelpText')}
                        >
                            <BooleanField {...field} value={field.value ? 'true' : ''} />
                        </FormField>
                    )}
                />

                <Controller
                    name="integrationParameters"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('ordat.form.integrationParameters')}
                            error={formErrors.integrationParameters}
                            helpText={
                                <span>
                                    {t('ordat.form.integrationParametersHelpText')}
                                    {parameterDefinitions.length > 0 && (
                                        <ul className="list-disc pl-4 mt-1">
                                            {parameterDefinitions.map((definition) => (
                                                <li key={definition.key}>
                                                    <code>{definition.key}</code> ({definition.type},{' '}
                                                    {t('ordat.form.integrationParameterDefault')}:{' '}
                                                    <code>{definition.default ?? 'null'}</code>) —{' '}
                                                    {definition.description}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </span>
                            }
                        >
                            <JsonField value={field.value} onValueChange={(value) => field.onChange(value)} />
                        </FormField>
                    )}
                />
            </div>
        </details>
    );
};

export default OrdatAdvancedFields;
